import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Paper,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import ServiceRequests from '../../components/Admin/ServiceRequests';
import Orders from '../../components/Admin/Orders';
import ProductRequests from '../../components/Admin/ProductRequests';
import { logoutAdmin } from '../../utils/auth';

function TabPanel({ children, value, index }) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ py: 3 }}>
      {value === index && children}
    </Box>
  );
}

function AdminDashboard() {
  const navigate = useNavigate();
  const [tabValue, setTabValue]     = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshKey, setRefreshKey] = useState(0); // used as prop to trigger child reloads
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  // Re-load data whenever the browser tab becomes visible again
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        setRefreshKey(k => k + 1);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const handleTabChange = (_, newValue) => setTabValue(newValue);
  const handleSearch    = (e) => setSearchQuery(e.target.value);

  const handleRefresh = () => {
    setRefreshKey(k => k + 1);
    setNotification({ open: true, message: 'Data refreshed', severity: 'success' });
  };

  const handleLogout = async () => {
    await logoutAdmin();
    navigate('/admin/login');
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography 
            variant="h3" 
            component="h1"
            sx={{
              background: 'linear-gradient(45deg, #ff6b6b 30%, #ffd93d 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
            }}
          >
            Admin Dashboard
          </Typography>
          <Button
            variant="outlined"
            onClick={handleLogout}
            sx={{
              color: '#ff6b6b',
              borderColor: '#ff6b6b',
              '&:hover': {
                borderColor: '#ff5252',
                background: 'rgba(255, 107, 107, 0.1)',
              },
            }}
          >
            Logout
          </Button>
        </Box>

        <Paper 
          sx={{ 
            mb: 4,
            p: 2,
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            borderRadius: '15px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by name, email, or order ID..."
              value={searchQuery}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: 'rgba(255, 255, 255, 0.05)',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.08)',
                  },
                },
              }}
            />
            <IconButton 
              onClick={handleRefresh}
              sx={{ 
                color: 'primary.main',
                '&:hover': {
                  background: 'rgba(255, 107, 107, 0.1)',
                },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        </Paper>

        <Paper 
          sx={{ 
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            borderRadius: '15px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: 'primary.main',
                },
              },
            }}
          >
            <Tab label="Service Requests" />
            <Tab label="Orders" />
            <Tab label="Product Requests" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <ServiceRequests searchQuery={searchQuery} refreshKey={refreshKey} />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <Orders searchQuery={searchQuery} refreshKey={refreshKey} />
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <ProductRequests searchQuery={searchQuery} refreshKey={refreshKey} />
          </TabPanel>
        </Paper>
      </Container>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert 
          onClose={() => setNotification({ ...notification, open: false })} 
          severity={notification.severity}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AdminDashboard;
